import type { inferRouterOutputs } from "@trpc/server";
import { Link, useNavigate, useParams } from "react-router";

import type { AppRouter } from "@runcheetah/api/router";
import { CodeText, InfoTile, MoneyValue, PageHeader, QueryErrorState } from "@/components/common";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { StatusBadge } from "@/pages/brands";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type UserDetail = RouterOutputs["admin"]["users"]["get"];
type UserBrand = UserDetail["brands"][number];
type UserActivity = UserDetail["activity"][number];

const formatDate = formatDateTime;

export function buildDeleteUserConfirmationMessage(user: { email: string }): string {
  return `Permanently delete ${user.email} and all owned brands? This deletes the user's auth data, sessions, brand records, generated content, and brand storage objects.`;
}

function UserSummaryCard({ user }: { user: UserDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>User summary</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <InfoTile label="Email" value={user.email} />
        <InfoTile label="Name" value={user.name ?? "-"} />
        <InfoTile label="Joined" value={formatDate(user.createdAt)} />
        <InfoTile label="User ID">
          <CodeText className="mt-1 block">{user.id}</CodeText>
        </InfoTile>
      </CardContent>
    </Card>
  );
}

function SpendSummaryCard({ user }: { user: UserDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Spend across brands</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-3">
        <InfoTile label="Total estimated spend">
          <p className="mt-1 text-2xl font-semibold">
            <MoneyValue amount={user.totalCostUsd} />
          </p>
        </InfoTile>
        <InfoTile label="Owned brands" value={String(user.brands.length)} />
        <InfoTile label="Usage events" value={String(user.usageEventCount)} />
      </CardContent>
    </Card>
  );
}

function BrandRow({ brand }: { brand: UserBrand }) {
  return (
    <Link
      to={`/brands/${brand.id}`}
      className="grid gap-3 rounded-xl border p-4 transition-colors hover:bg-muted/40 md:grid-cols-[minmax(0,1.2fr)_auto_auto] md:items-center"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate font-medium">{brand.sourceDomain}</p>
          <StatusBadge status={brand.status} />
        </div>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          Linked {formatDate(brand.linkedAt)}
        </p>
      </div>
      <div className="text-sm text-muted-foreground">
        {brand.usageEventCount} usage event{brand.usageEventCount === 1 ? "" : "s"}
      </div>
      <div className="text-sm font-medium">
        <MoneyValue amount={brand.totalCostUsd} />
      </div>
    </Link>
  );
}

function OwnedBrandsCard({ brands }: { brands: UserBrand[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Owned brands</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {brands.length > 0 ? (
          brands.map((brand) => <BrandRow key={brand.id} brand={brand} />)
        ) : (
          <p className="text-sm text-muted-foreground">No brands linked to this user.</p>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityRow({ item }: { item: UserActivity }) {
  return (
    <div className="rounded-xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
              {item.kind.replace("_", " ")}
            </span>
            <Link to={`/brands/${item.brandId}`} className="text-sm font-medium hover:underline">
              {item.sourceDomain}
            </Link>
          </div>
          <p className="mt-2 text-sm font-medium">{item.title}</p>
          {item.description ? (
            <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
          ) : null}
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <p>{formatDate(item.occurredAt)}</p>
          {item.costUsd !== null ? (
            <p className="mt-1 font-medium text-foreground">
              <MoneyValue amount={item.costUsd} precision={6} />
            </p>
          ) : item.kind === "llm_usage" ? (
            <p className="mt-1 font-medium text-amber-700">Unknown cost</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ActivityCard({ activity }: { activity: UserActivity[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity log</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {activity.length > 0 ? (
          activity.map((item) => <ActivityRow key={item.id} item={item} />)
        ) : (
          <p className="text-sm text-muted-foreground">
            No activity recorded for this user's brands.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function UserDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data, isPending, error } = trpc.admin.users.get.useQuery({ id: id! }, { enabled: !!id });
  const deleteMutation = trpc.admin.users.delete.useMutation({
    onSuccess: () => {
      void utils.admin.users.list.invalidate().catch(() => undefined);
      navigate("/users");
    },
  });

  const handleDelete = () => {
    if (!id || !data) return;

    if (window.confirm(buildDeleteUserConfirmationMessage(data))) {
      deleteMutation.mutate({ id });
    }
  };

  if (isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="User" meta="Loading..." />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <QueryErrorState title="Failed to load user" message={error?.message ?? "User not found"} />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.email}
        meta={`${data.brands.length} owned brands`}
        actions={
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? "Deleting..." : "Delete user"}
          </Button>
        }
      />
      {deleteMutation.error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {deleteMutation.error.message}
        </div>
      ) : null}
      <UserSummaryCard user={data} />
      <SpendSummaryCard user={data} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <OwnedBrandsCard brands={data.brands} />
        <ActivityCard activity={data.activity} />
      </div>
    </div>
  );
}
